<template>
  <v-container fluid class="pa-0 ma-0">
    <v-row justify="center">
      <v-col cols="12" md="10" lg="9">
        <v-card class="pa-6 pa-md-10 elevation-2">
          <!-- Header -->
          <div class="text-center mb-8">
            <v-avatar color="primary" size="64" class="mb-4">
              <v-icon icon="$mdiShieldLock" size="32" color="white"></v-icon>
            </v-avatar>
            <h1 class="text-h4 font-weight-bold mb-2">
              Políticas de Privacidad
            </h1>
            <p class="text-subtitle-1 text-medium-emphasis">
              Última actualización: 10/05/2025
            </p>
          </div>

          <v-divider class="mb-8"></v-divider>

          <!-- Introduction -->
          <section class="mb-8">
            <p class="text-body-1 mb-4">
              Bienvenido a <strong>Truster Cloud</strong>, un ecosistema ERP
              desarrollado por <strong>Truster Consulting, C.A.</strong>.
            </p>
            <p class="text-body-1 mb-4">
              Esta Política de Privacidad describe cómo recopilamos, utilizamos,
              procesamos y protegemos la información personal y empresarial a
              través de nuestro sitio web, nuestro software ERP (SaaS) y nuestra
              aplicación móvil <strong>Truster Cloud Sales</strong>.
            </p>

            <h3 class="text-h6 font-weight-bold mb-2">Definiciones Clave</h3>
            <p class="text-body-1 mb-4">
              Para efectos de esta política, distinguimos dos tipos de actores
              principales:
            </p>
            <ul class="ml-6 mb-4">
              <li class="mb-2">
                <strong>El Cliente:</strong> Se refiere a la empresa,
                organización o entidad comercial que contrata los servicios de
                Truster Cloud. El Cliente es el propietario de la información
                comercial y quien autoriza el acceso a sus empleados.
              </li>
              <li>
                <strong>El Usuario:</strong> Se refiere a la persona natural
                (empleado, vendedor, administrador) autorizada por El Cliente
                para acceder a la plataforma y utilizar sus funcionalidades bajo
                las credenciales asignadas.
              </li>
            </ul>

            <v-alert
              type="info"
              variant="tonal"
              class="mb-4 text-body-2"
              icon="$mdiInformation"
            >
              <strong>Nota sobre Responsabilidades:</strong> Truster Cloud actúa
              como proveedor de tecnología. <strong>El Cliente</strong> mantiene
              la propiedad y control sobre sus datos comerciales (Responsable
              del Tratamiento), mientras que nosotros procesamos dicha
              información únicamente para prestar el servicio contratado.
            </v-alert>
          </section>

          <!-- 1. Marco Legal -->
          <section class="mb-8">
            <h2 class="text-h5 font-weight-bold mb-4 d-flex align-center">
              <v-icon
                icon="$mdiGavel"
                start
                color="primary"
                size="small"
              ></v-icon>
              1. Marco Legal y Cumplimiento
            </h2>
            <v-card-text class="text-body-1 pt-0 pl-0">
              <p class="mb-3">
                Estas políticas se rigen por la legislación de la
                <strong>República Bolivariana de Venezuela</strong>, cumpliendo
                específicamente con:
              </p>
              <ul class="ml-6 mb-4">
                <li>
                  <strong>Constitución de la República (Artículo 28):</strong>
                  Garantizando el derecho constitucional de Habeas Data (acceso
                  a la información).
                </li>
                <li>
                  <strong>Ley Especial contra los Delitos Informáticos:</strong>
                  Protegiendo la integridad, confidencialidad y disponibilidad
                  de los sistemas y datos.
                </li>
                <li>
                  <strong>Estándares Internacionales:</strong> Adoptamos
                  principios del Reglamento General de Protección de Datos
                  (RGPD) como referencia de buenas prácticas en seguridad y
                  tratamiento de datos.
                </li>
              </ul>
            </v-card-text>
          </section>

          <!-- 2. Información que Recopilamos -->
          <section class="mb-8">
            <h2 class="text-h5 font-weight-bold mb-4 d-flex align-center">
              <v-icon
                icon="$mdiDatabaseSearch"
                start
                color="primary"
                size="small"
              ></v-icon>
              2. Información que Recopilamos
            </h2>
            <v-card-text class="text-body-1 pt-0 pl-0">
              <p class="mb-3">
                Recopilamos información en tres categorías principales:
              </p>

              <h3 class="text-h6 font-weight-medium mb-2 mt-4">
                A. Información Proporcionada por el Cliente
              </h3>
              <p class="mb-2">
                Datos necesarios para la creación de cuentas y operación del
                ERP:
              </p>
              <ul class="ml-6 mb-4">
                <li>
                  Datos de identificación (Nombre, Cédula de Identidad/RIF).
                </li>
                <li>
                  Datos de contacto (Correo electrónico corporativo, teléfono).
                </li>
                <li>
                  Credenciales de acceso (Usuario y contraseñas cifradas).
                </li>
                <li>
                  Datos transaccionales del negocio (Ventas, inventario,
                  facturación).
                </li>
              </ul>

              <h3 class="text-h6 font-weight-medium mb-2 mt-4">
                B. Información Recopilada Automáticamente
              </h3>
              <p class="mb-2">
                Al utilizar nuestra plataforma web o sitio público:
              </p>
              <ul class="ml-6 mb-4">
                <li>
                  Registros de actividad (Logs) y direcciones IP por seguridad.
                </li>
                <li>
                  Datos del dispositivo y navegador para optimización de la
                  experiencia de usuario.
                </li>
                <li>Cookies técnicas y analíticas (Google Analytics).</li>
              </ul>

              <h3 class="text-h6 font-weight-medium mb-2 mt-4">
                C. Datos de la App Móvil (Truster Cloud Sales)
              </h3>
              <v-card variant="outlined" class="pa-4 mt-2 bg-grey-lighten-5">
                <div class="d-flex align-start">
                  <v-icon
                    icon="$mdiMapMarkerRadius"
                    color="secondary"
                    class="mt-1 mr-3"
                  ></v-icon>
                  <div>
                    <strong class="d-block mb-1"
                      >Geolocalización (Solo Primer Plano)</strong
                    >
                    <p class="text-body-2 mb-0">
                      Nuestra aplicación recopila datos precisos de ubicación
                      (latitud y longitud) <strong>únicamente</strong> cuando el
                      usuario realiza una acción explícita de "Check-in" o
                      "Visita" en la ruta asignada. <br /><br />
                      <strong
                        >No realizamos rastreo en segundo plano
                        (background).</strong
                      >
                      La ubicación no se monitorea cuando la aplicación está
                      cerrada o no está en uso activo. Estos datos se utilizan
                      exclusivamente para validar la ejecución de rutas
                      comerciales y logísticas.
                    </p>
                  </div>
                </div>
              </v-card>
            </v-card-text>
          </section>

          <!-- 3. Uso de la Información -->
          <section class="mb-8">
            <h2 class="text-h5 font-weight-bold mb-4 d-flex align-center">
              <v-icon
                icon="$mdiCogs"
                start
                color="primary"
                size="small"
              ></v-icon>
              3. Finalidad del Tratamiento
            </h2>
            <v-card-text class="text-body-1 pt-0 pl-0">
              <p class="mb-2">Utilizamos sus datos estrictamente para:</p>
              <ul class="ml-6 mb-4">
                <li>
                  <strong>Prestación del Servicio:</strong> Operar el ERP,
                  procesar pedidos, facturación y logística.
                </li>
                <li>
                  <strong>Soporte Técnico:</strong> Diagnosticar errores y
                  asistir a los usuarios.
                </li>
                <li>
                  <strong>Seguridad:</strong> Detectar y prevenir accesos no
                  autorizados o fraudes.
                </li>
                <li>
                  <strong>Mejora Continua:</strong> Analizar métricas de uso
                  para optimizar el rendimiento (Datos anonimizados).
                </li>
              </ul>
            </v-card-text>
          </section>

          <!-- 4. Compartir Datos y Terceros -->
          <section class="mb-8">
            <h2 class="text-h5 font-weight-bold mb-4 d-flex align-center">
              <v-icon
                icon="$mdiShareVariant"
                start
                color="primary"
                size="small"
              ></v-icon>
              4. Transferencia de Datos y Proveedores
            </h2>
            <v-card-text class="text-body-1 pt-0 pl-0">
              <p class="mb-4">
                No comercializamos sus datos. Compartimos información únicamente
                con proveedores de servicios esenciales que nos ayudan a operar
                nuestra plataforma, bajo estrictos acuerdos de confidencialidad.
                Para proteger la seguridad de nuestra infraestructura, no
                revelamos la lista detallada de proveedores, pero categorizamos
                sus funciones a continuación:
              </p>
              <v-table density="compact" class="mb-4 border rounded">
                <thead>
                  <tr>
                    <th class="text-left">Categoría de Servicio</th>
                    <th class="text-left">Propósito</th>
                    <th class="text-left">Ubicación</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Infraestructura en la Nube (IaaS)</td>
                    <td>
                      Alojamiento de servidores, bases de datos y almacenamiento
                      seguro.
                    </td>
                    <td>Internacional</td>
                  </tr>
                  <tr>
                    <td>Seguridad y CDN</td>
                    <td>
                      Protección contra ataques, firewall y optimización de
                      entrega de contenido.
                    </td>
                    <td>Global</td>
                  </tr>
                  <tr>
                    <td>Servicios de Geolocalización</td>
                    <td>
                      Visualización de mapas, cálculo de rutas y validación de
                      direcciones.
                    </td>
                    <td>Internacional</td>
                  </tr>
                  <tr>
                    <td>Comunicaciones y Notificaciones</td>
                    <td>
                      Envío de alertas, correos electrónicos transaccionales y
                      mensajería SMS.
                    </td>
                    <td>Internacional</td>
                  </tr>
                  <tr>
                    <td>Inteligencia de Negocios (BI)</td>
                    <td>
                      Herramientas de análisis de datos y generación de reportes
                      avanzados.
                    </td>
                    <td>Internacional</td>
                  </tr>
                  <tr>
                    <td>Pasarelas de Pago y Banca</td>
                    <td>
                      Procesamiento seguro de transacciones financieras (según
                      integraciones activas).
                    </td>
                    <td>Venezuela / Internacional</td>
                  </tr>
                  <tr>
                    <td>Logística Externa</td>
                    <td>
                      Integración con servicios de encomiendas para gestión de
                      envíos.
                    </td>
                    <td>Venezuela</td>
                  </tr>
                </tbody>
              </v-table>
              <p class="text-caption text-grey-darken-1">
                * Al utilizar nuestros servicios, usted consiente la
                transferencia de datos necesaria para la operación técnica con
                estos proveedores, quienes cumplen con estándares de seguridad
                internacionales.
              </p>
            </v-card-text>
          </section>

          <!-- 5. Seguridad -->
          <section class="mb-8">
            <h2 class="text-h5 font-weight-bold mb-4 d-flex align-center">
              <v-icon
                icon="$mdiSecurity"
                start
                color="primary"
                size="small"
              ></v-icon>
              5. Seguridad de la Información
            </h2>
            <v-card-text class="text-body-1 pt-0 pl-0">
              <p class="mb-4">
                Implementamos medidas de seguridad de nivel industrial para
                proteger sus datos:
              </p>
              <v-row>
                <v-col cols="12" sm="6">
                  <v-list-item
                    prepend-icon="$mdiLockCheck"
                    title="Cifrado SSL/TLS"
                    subtitle="En todas las comunicaciones de datos."
                  ></v-list-item>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-list-item
                    prepend-icon="$mdiDatabaseLock"
                    title="Cifrado en Reposo"
                    subtitle="Para datos sensibles y contraseñas."
                  ></v-list-item>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-list-item
                    prepend-icon="$mdiAccountKey"
                    title="Control de Acceso"
                    subtitle="Autenticación estricta y roles de usuario."
                  ></v-list-item>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-list-item
                    prepend-icon="$mdiBackupRestore"
                    title="Respaldos Diarios"
                    subtitle="Para garantizar la disponibilidad."
                  ></v-list-item>
                </v-col>
              </v-row>
            </v-card-text>
          </section>

          <!-- 6. Derechos ARCO -->
          <section class="mb-8">
            <h2 class="text-h5 font-weight-bold mb-4 d-flex align-center">
              <v-icon
                icon="$mdiAccountBoxMultiple"
                start
                color="primary"
                size="small"
              ></v-icon>
              6. Derechos y Control de Datos
            </h2>
            <v-card-text class="text-body-1 pt-0 pl-0">
              <p class="mb-4">
                Reconocemos los derechos tanto de nuestros Clientes como de los
                Usuarios finales sobre la información gestionada en la
                plataforma.
              </p>

              <h3 class="text-h6 font-weight-medium mb-2">
                Derechos del Cliente (Empresa)
              </h3>
              <p class="mb-4">
                Como suscriptor de la instancia y propietario de los datos comerciales, El
                Cliente tiene derecho a:
              </p>
              <ul class="ml-6 mb-4">
                <li>
                  Solicitar la exportación completa de su base de datos en
                  formatos estándar.
                </li>
                <li>
                  Gestionar, auditar y revocar los accesos de sus Usuarios en
                  cualquier momento.
                </li>
                <li>
                  Solicitar el borrado seguro de sus datos al finalizar la
                  relación contractual.
                </li>
              </ul>

              <h3 class="text-h6 font-weight-medium mb-2">
                Derechos del Usuario (Empleado)
              </h3>
              <p class="mb-4">
                Como titular de datos personales, El Usuario tiene derecho al
                <strong
                  >Acceso, Rectificación, Cancelación y Oposición (ARCO)</strong
                >.
              </p>
              <div class="bg-blue-grey-lighten-5 pa-4 rounded">
                <p class="mb-2 font-weight-bold">
                  Procedimiento para Usuarios:
                </p>
                <p class="mb-0 text-body-2">
                  Dado que su cuenta es gestionada por
                  <strong>El Cliente</strong> (su empleador), debe dirigir
                  cualquier solicitud de eliminación o modificación de datos
                  directamente al Administrador del Sistema de su empresa.
                  Truster Cloud provee las herramientas técnicas para que El
                  Cliente gestione estas solicitudes, pero no podemos alterar
                  datos sin la instrucción expresa del suscriptor de la
                  instancia.
                </p>
              </div>
            </v-card-text>
          </section>

          <!-- 7. Contacto -->
          <section class="mb-0">
            <h2 class="text-h5 font-weight-bold mb-4 d-flex align-center">
              <v-icon
                icon="$mdiEmailCheck"
                start
                color="primary"
                size="small"
              ></v-icon>
              7. Contacto
            </h2>
            <v-card-text class="text-body-1 pt-0 pl-0">
              <p class="mb-4">
                Para consultas relacionadas específicamente con esta Política de
                Privacidad o nuestras prácticas de seguridad, puede contactar a
                nuestro equipo:
              </p>
              <v-row>
                <v-col cols="12" md="6">
                  <v-card variant="outlined" class="h-100 pa-4">
                    <v-card-text>
                      <div class="text-caption mb-1">Correo Electrónico</div>
                      <div class="text-h6 text-primary">
                        <a
                          href="mailto:soporte@trusterconsulting.com"
                          class="text-decoration-none text-primary"
                          >soporte@trusterconsulting.com</a
                        >
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" md="6">
                  <v-card variant="outlined" class="h-100 pa-4">
                    <v-card-text>
                      <div class="text-caption mb-1">Dirección Legal</div>
                      <div class="text-body-1">{{ companyInfo.address }}</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-card-text>
          </section>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script lang="ts" setup>
import companyInfo from "~/data/companyInfo";

useSeoMeta({
  title: "Políticas de Privacidad",
  description:
    "Políticas de privacidad y protección de datos de Truster Cloud. Conozca cómo gestionamos y protegemos su información empresarial.",
  ogTitle: "Políticas de Privacidad - Truster Cloud",
  ogDescription:
    "Compromiso de seguridad y privacidad de Truster Consulting C.A.",
});
</script>

<style scoped>
ul {
  list-style-type: disc;
}
.v-card-text {
  line-height: 1.6;
}
</style>
